import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { librariesApi, libraryLocationsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { BorrowingRule, MemberType } from '@/types/library';
import { orDash } from '@/utils/format';
import { Field, Money, humanise } from './shared';

const MEMBER_TYPES: MemberType[] = ['STUDENT', 'TEACHER', 'STAFF'];

/**
 * How the library behaves.
 *
 * <p>Two layers, and the split matters. The library carries a default for
 * everything; a borrowing rule overrides it for one kind of member. A rule field
 * left empty falls back to the library's own figure rather than meaning zero —
 * "no fine set" and "a fine of nothing" are different statements, and conflating
 * them is how a school ends up silently not fining anybody.
 *
 * <p>Every figure here is read by the server when it decides something. Changing
 * a loan period changes what the next issue is due back, not what is already
 * out: a loan's due date was decided when it was issued and does not move under
 * the reader.
 */
export function LibrarySettingsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.LIBRARY_MANAGE);

  const libraries = useQuery({
    queryKey: queryKeys.library.list,
    queryFn: () => librariesApi.list(),
  });

  const library = libraries.data?.[0];

  const locations = useQuery({
    queryKey: queryKeys.library.locations(library?.id),
    queryFn: () => libraryLocationsApi.list(library?.id),
    enabled: library !== undefined,
  });

  const [editingRule, setEditingRule] = useState<MemberType | null>(null);
  const [draft, setDraft] = useState({
    maxBooks: '',
    loanDays: '',
    maxRenewals: '',
    finePerOverdueDay: '',
    maxFinePerLoan: '',
    gracePeriodDays: '',
  });

  useEffect(() => {
    if (editingRule === null || library === undefined) {
      return;
    }
    const rule = library.borrowingRules.find((row) => row.memberType === editingRule);
    setDraft({
      maxBooks: String(rule?.maxBooks ?? library.defaultMaxBooks),
      loanDays: String(rule?.loanDays ?? library.defaultLoanDays),
      maxRenewals: String(rule?.maxRenewals ?? library.defaultMaxRenewals),
      finePerOverdueDay: rule?.finePerOverdueDay === undefined ? '' : String(rule.finePerOverdueDay),
      maxFinePerLoan: rule?.maxFinePerLoan === undefined ? '' : String(rule.maxFinePerLoan),
      gracePeriodDays: rule?.gracePeriodDays === undefined ? '' : String(rule.gracePeriodDays),
    });
  }, [editingRule, library]);

  const saveRule = useMutation({
    mutationFn: () =>
      librariesApi.saveRule(library!.id, {
        memberType: editingRule!,
        maxBooks: Number(draft.maxBooks),
        loanDays: Number(draft.loanDays),
        maxRenewals: Number(draft.maxRenewals),
        // Empty means "use the library's figure", which is not the same as zero.
        finePerOverdueDay: draft.finePerOverdueDay === '' ? null : Number(draft.finePerOverdueDay),
        maxFinePerLoan: draft.maxFinePerLoan === '' ? null : Number(draft.maxFinePerLoan),
        gracePeriodDays: draft.gracePeriodDays === '' ? null : Number(draft.gracePeriodDays),
      }),
    onSuccess: () => {
      toast.success('Rule saved. It applies to the next book issued, not to loans already out.');
      setEditingRule(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (libraries.isError) {
    return <ErrorState error={libraries.error} onRetry={() => void libraries.refetch()} />;
  }
  if (libraries.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (library === undefined) {
    return (
      <Card className="p-6 text-center text-sm text-muted">
        This school has no library yet.
      </Card>
    );
  }

  return (
    <div>
      <PageHeader
        title="Library settings"
        description="Loan periods, limits and fines — the figures the server reads when it decides something."
        icon={<Settings2 className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            {library.name}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            The defaults. A borrowing rule for a member type overrides them.
          </p>
          <dl className="mt-2">
            <Field label="Loan period">{library.defaultLoanDays} days</Field>
            <Field label="Books at once">{library.defaultMaxBooks}</Field>
            <Field label="Renewals">{library.defaultMaxRenewals}</Field>
            <Field label="Grace period">{library.gracePeriodDays} days</Field>
            <Field label="Fine per day">
              <Money value={library.finePerOverdueDay} />
            </Field>
            <Field label="Blocks borrowing at">
              {library.blockBorrowingOnFines ? (
                <Money value={library.fineBlockThreshold} />
              ) : (
                'Fines never block borrowing'
              )}
            </Field>
            <Field label="Holds kept for">{library.reservationHoldDays} days</Field>
            <Field label="Reservations lapse after">{library.reservationExpiryDays} days</Field>
            <Field label="Lost book charge">
              {library.lostBookMultiplier}× what it cost, or{' '}
              <Money value={library.lostBookFlatFee} /> flat
            </Field>
            <Field label="Damage charge">
              <Money value={library.damagedBookFlatFee} />
            </Field>
            <Field label="Where">{orDash(library.location)}</Field>
            <Field label="Open">{orDash(library.openingHours)}</Field>
          </dl>
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              What each kind of member may do
            </h2>
            <p className="mt-0.5 text-xs text-muted">
              A blank fine field means "use the library's figure" — not a fine of nothing.
            </p>

            <div className="mt-3 space-y-3">
              {MEMBER_TYPES.map((memberType) => {
                const rule = library.borrowingRules.find((row) => row.memberType === memberType);
                return (
                  <div key={memberType} className="border border-line p-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-bold capitalize text-ink">
                        {humanise(memberType)}s
                      </h3>
                      {manages && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            setEditingRule(editingRule === memberType ? null : memberType)
                          }
                        >
                          {editingRule === memberType ? 'Close' : 'Change'}
                        </Button>
                      )}
                    </div>

                    {editingRule === memberType ? (
                      <div className="mt-3 space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <TextField
                            label="Books"
                            type="number"
                            min={1}
                            value={draft.maxBooks}
                            onChange={(event) =>
                              setDraft((current) => ({ ...current, maxBooks: event.target.value }))
                            }
                          />
                          <TextField
                            label="Days"
                            type="number"
                            min={1}
                            value={draft.loanDays}
                            onChange={(event) =>
                              setDraft((current) => ({ ...current, loanDays: event.target.value }))
                            }
                          />
                          <TextField
                            label="Renewals"
                            type="number"
                            min={0}
                            value={draft.maxRenewals}
                            onChange={(event) =>
                              setDraft((current) => ({
                                ...current,
                                maxRenewals: event.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <TextField
                            label="Per day"
                            value={draft.finePerOverdueDay}
                            onChange={(event) =>
                              setDraft((current) => ({
                                ...current,
                                finePerOverdueDay: event.target.value,
                              }))
                            }
                          />
                          <TextField
                            label="Cap"
                            value={draft.maxFinePerLoan}
                            onChange={(event) =>
                              setDraft((current) => ({
                                ...current,
                                maxFinePerLoan: event.target.value,
                              }))
                            }
                          />
                          <TextField
                            label="Grace days"
                            value={draft.gracePeriodDays}
                            onChange={(event) =>
                              setDraft((current) => ({
                                ...current,
                                gracePeriodDays: event.target.value,
                              }))
                            }
                          />
                        </div>
                        <Button onClick={() => saveRule.mutate()} disabled={saveRule.isPending}>
                          Save the rule
                        </Button>
                      </div>
                    ) : (
                      <RuleSummary rule={rule} />
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Shelves</h2>
            <p className="mt-0.5 text-xs text-muted">
              Where copies live. The path is stored whole, so a listing needs no tree walk.
            </p>
            {locations.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : (locations.data ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">No shelves defined.</p>
            ) : (
              <ul className="mt-2 divide-y divide-line">
                {(locations.data ?? []).map((row) => (
                  <li key={row.id} className="flex items-baseline justify-between gap-3 py-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">{row.fullPath}</span>
                      <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
                    </span>
                    <span className="shrink-0 text-xs text-muted">
                      {row.copyCount} cop{row.copyCount === 1 ? 'y' : 'ies'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function RuleSummary({ rule }: { rule: BorrowingRule | undefined }) {
  if (rule === undefined) {
    return <p className="mt-1 text-sm text-muted">Uses the library's defaults.</p>;
  }
  return (
    <p className="mt-1 text-sm text-muted">
      {rule.maxBooks} book{rule.maxBooks === 1 ? '' : 's'} for {rule.loanDays} days,{' '}
      {rule.maxRenewals} renewal{rule.maxRenewals === 1 ? '' : 's'}.{' '}
      {rule.finePerOverdueDay === undefined
        ? "The library's fine applies."
        : `${rule.finePerOverdueDay} a day`}
      {rule.gracePeriodDays !== undefined && ` after ${rule.gracePeriodDays} days' grace`}
      {rule.maxFinePerLoan !== undefined && `, capped at ${rule.maxFinePerLoan}`}.
      {!rule.canReserve && ' Cannot reserve.'}
    </p>
  );
}
